import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { GoogleAuthButton } from '../components/GoogleAuthButton.tsx';
import { AppleAuthButton } from '../components/AppleAuthButton.tsx';
import { MicrosoftAuthButton } from '../components/MicrosoftAuthButton.tsx';
import { authAPI } from '../services/api.ts';

interface FormData {
  customerName: string;
  customerAddress: string;
  userName: string;
  email: string;
  password: string;
  confirmPassword: string;
  verificationCode: string;
}

interface FormErrors {
  customerName?: string;
  customerAddress?: string;
  userName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  verificationCode?: string;
  general?: string;
}

type SignupStep = 'email' | 'verify' | 'form';

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [currentStep, setCurrentStep] = useState<SignupStep>('email');
  const [formData, setFormData] = useState<FormData>({
    customerName: '',
    customerAddress: '',
    userName: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);

  // Check for OAuth signup parameters
  const isGoogleSignup = searchParams.get('error') === 'no_tenancy' && searchParams.get('googleId');
  const isAppleSignup = searchParams.get('error') === 'no_tenancy' && searchParams.get('appleId');
  const isMicrosoftSignup = searchParams.get('error') === 'no_tenancy' && searchParams.get('microsoftId');
  const isOAuthSignup = isGoogleSignup || isAppleSignup || isMicrosoftSignup;
  
  React.useEffect(() => {
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const googleId = searchParams.get('googleId');
    const microsoftId = searchParams.get('microsoftId');
    const error = searchParams.get('error');

    if (error === 'no_tenancy' && email && name) {
      // Pre-fill form with OAuth data
      setFormData(prev => ({
        ...prev,
        email: email,
        userName: name,
        // Don't set password for OAuth signups
      }));
      const authProvider = googleId ? 'Google' : (microsoftId ? 'Microsoft' : 'Apple');
      setErrors({ general: `✅ ${authProvider} authentication successful! Please complete your company details to finish setting up your account.` });
    }
  }, [searchParams]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    } else if (formData.customerName.trim().length < 2) {
      newErrors.customerName = 'Customer name must be at least 2 characters';
    }

    if (!formData.customerAddress.trim()) {
      newErrors.customerAddress = 'Customer address is required';
    } else if (formData.customerAddress.trim().length < 10) {
      newErrors.customerAddress = 'Address must be at least 10 characters';
    }

    if (!formData.userName.trim()) {
      newErrors.userName = 'User name is required';
    } else if (formData.userName.trim().length < 2) {
      newErrors.userName = 'User name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Only validate passwords for regular signups, not Google signups
    if (!isOAuthSignup) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleGoogleSignup = () => {
    console.log('=== GOOGLE SIGNUP DEBUG ===');
    console.log('Google signup button clicked!');
    console.log('Redirecting to:', '/api/auth/google');
    
    // Use absolute URL to ensure we hit the backend
    const backendUrl = 'https://plantedhire.com/api/auth/google';
    console.log('Using absolute URL:', backendUrl);
    window.location.href = backendUrl;
  };

  const handleAppleSignup = () => {
    console.log('=== APPLE SIGNUP DEBUG ===');
    console.log('Apple signup button clicked!');
    console.log('Current URL:', window.location.href);
    
    // Use the working configuration (no scopes, query mode)
    const appleAuthUrl = `https://appleid.apple.com/auth/authorize?` +
      `client_id=${encodeURIComponent('com.c2s.planted.si')}&` +
      `redirect_uri=${encodeURIComponent('https://plantedhire.com/api/auth/apple/callback')}&` +
      `response_type=code&` +
      `state=signup&` +
      `response_mode=query`;
    
    console.log('Redirecting to Apple:', appleAuthUrl);
    window.location.href = appleAuthUrl;
  };

  const handleMicrosoftSignup = () => {
    console.log('=== MICROSOFT SIGNUP DEBUG ===');
    console.log('Microsoft signup button clicked!');
    console.log('Current URL:', window.location.href);
    
    // Use localhost for the initial redirect
    const backendUrl = 'https://plantedhire.com/api/auth/microsoft';
    console.log('Using localhost URL:', backendUrl);
    window.location.href = backendUrl;
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!formData.email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsSendingCode(true);
    setErrors({});

    try {
      await authAPI.requestVerificationCode(formData.email.trim());
      setCodeSent(true);
      setCurrentStep('verify');
    } catch (error: any) {
      console.error('Request code error:', error);
      setErrors({ 
        general: error.response?.data?.error || 'Failed to send verification code. Please try again.' 
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate code
    if (!formData.verificationCode.trim()) {
      setErrors({ verificationCode: 'Verification code is required' });
      return;
    }
    
    if (formData.verificationCode.trim().length !== 6) {
      setErrors({ verificationCode: 'Code must be 6 digits' });
      return;
    }

    setIsVerifyingCode(true);
    setErrors({});

    try {
      await authAPI.verifyCode(formData.email.trim(), formData.verificationCode.trim());
      setCodeVerified(true);
      setCurrentStep('form');
    } catch (error: any) {
      console.error('Verify code error:', error);
      setErrors({ 
        verificationCode: error.response?.data?.error || 'Invalid or expired code. Please try again.' 
      });
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Ensure code is verified
    if (!codeVerified) {
      setErrors({ general: 'Please verify your email first' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Check if this is a Google signup completion
      const isOAuthSignup = searchParams.get('error') === 'no_tenancy';
      
      if (isOAuthSignup) {
        // Handle OAuth signup completion (Google, Microsoft, or Apple)
        const googleId = searchParams.get('googleId');
        const appleId = searchParams.get('appleId');
        const microsoftId = searchParams.get('microsoftId');
        const endpoint = googleId ? '/api/auth/google/complete-signup' : 
                        (microsoftId ? '/api/auth/microsoft/complete-signup' : '/api/auth/apple/complete-signup');
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email.trim(),
            customerName: formData.customerName.trim(),
            customerAddress: formData.customerAddress.trim(),
            userName: formData.userName.trim(),
            googleId: googleId || null,
            appleId: appleId || null,
            microsoftId: microsoftId || null,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to complete OAuth signup');
        }

        const data = await response.json();
        
        // Store auth data
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Fetch full customer data
        const customerResponse = await fetch(`https://plantedhire.com/api/customers`, {
          headers: {
            'Authorization': `Bearer ${data.token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (customerResponse.ok) {
          const customerData = await customerResponse.json();
          localStorage.setItem('customer', JSON.stringify(customerData));
        } else {
          // Fallback to just storing the customer ID
          localStorage.setItem('customer', JSON.stringify({ id: data.user.customer_id }));
        }
        
        // Refresh profile data to ensure customer info is properly loaded
        // This is what the "Update Profile" button does that fixes the display
        try {
          const profileResponse = await fetch(`https://plantedhire.com/api/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${data.token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            localStorage.setItem('user', JSON.stringify(profileData.user));
            localStorage.setItem('customer', JSON.stringify(profileData.customer));
          }
        } catch (error) {
          console.error('Failed to refresh profile after signup:', error);
        }
        
        // Safari-specific handling: Use a more reliable redirect method
        if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
          // For Safari, use replace instead of href to avoid back button issues
          window.location.replace('/app/dashboard');
        } else {
          // For other browsers, use the standard method
          window.location.href = '/app/dashboard';
        }
      } else {
        // Regular signup with verification code
        await register({
          customerName: formData.customerName.trim(),
          customerAddress: formData.customerAddress.trim(),
          userName: formData.userName.trim(),
          email: formData.email.trim(),
          password: formData.password,
          verificationCode: formData.verificationCode.trim(),
        });

        // Redirect to dashboard on successful registration
        navigate('/app/dashboard');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      
      if (error.response?.data?.error) {
        setErrors({ general: error.response.data.error });
      } else if (error.response?.data?.errors) {
        // Handle validation errors from server
        const serverErrors: FormErrors = {};
        error.response.data.errors.forEach((err: any) => {
          if (err.param) {
            serverErrors[err.param as keyof FormErrors] = err.msg;
          }
        });
        setErrors(serverErrors);
      } else {
        setErrors({ general: error.message || 'Registration failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <img 
            src="/images/site_logo.png" 
            alt="Planted" 
            className="h-12 w-auto mx-auto"
          />
          <p className="mt-2 text-sm text-gray-600">Equipment Management Platform</p>
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Start your free trial today
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card">
          <div className="card-body">
            {/* Step 1: Email Input */}
            {currentStep === 'email' && !isOAuthSignup && (
              <form className="space-y-6" onSubmit={handleRequestCode}>
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Get Started</h3>
                  <p className="text-sm text-gray-600 mt-1">Enter your email to receive a verification code</p>
                </div>

                {errors.general && (
                  <div className="rounded-lg p-4 bg-danger-50 border border-danger-200">
                    <p className="text-sm text-danger-600">{errors.general}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`form-input ${errors.email ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                    placeholder="your.email@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isSendingCode}
                  />
                  {errors.email && (
                    <p className="form-error">{errors.email}</p>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSendingCode}
                    className="btn btn-primary w-full"
                  >
                    {isSendingCode ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending Code...
                      </div>
                    ) : (
                      'Send Verification Code'
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Verify Code */}
            {currentStep === 'verify' && !isOAuthSignup && (
              <form className="space-y-6" onSubmit={handleVerifyCode}>
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Verify Your Email</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    We sent a 6-digit code to <strong>{formData.email}</strong>
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentStep('email');
                      setCodeSent(false);
                      setErrors({});
                    }}
                    className="text-sm text-primary-600 hover:text-primary-500 mt-2"
                  >
                    Change email address
                  </button>
                </div>

                {errors.general && (
                  <div className="rounded-lg p-4 bg-danger-50 border border-danger-200">
                    <p className="text-sm text-danger-600">{errors.general}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="verificationCode" className="form-label">
                    Verification Code
                  </label>
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    className={`form-input text-center text-2xl tracking-widest ${errors.verificationCode ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                    placeholder="000000"
                    value={formData.verificationCode}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setFormData(prev => ({ ...prev, verificationCode: value }));
                      if (errors.verificationCode) {
                        setErrors(prev => ({ ...prev, verificationCode: undefined }));
                      }
                    }}
                    disabled={isVerifyingCode}
                  />
                  {errors.verificationCode && (
                    <p className="form-error">{errors.verificationCode}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Didn't receive the code?{' '}
                    <button
                      type="button"
                      onClick={handleRequestCode}
                      className="text-primary-600 hover:text-primary-500 underline"
                      disabled={isSendingCode}
                    >
                      Resend code
                    </button>
                  </p>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isVerifyingCode || formData.verificationCode.length !== 6}
                    className="btn btn-primary w-full"
                  >
                    {isVerifyingCode ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </div>
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Complete Registration Form */}
            {(currentStep === 'form' || isOAuthSignup) && (
              <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Social Auth Buttons - Top of Form (hidden during OAuth signup) */}
              {!isOAuthSignup && (
                <div className="space-y-3">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-3">Sign up with your preferred account</p>
                  </div>
                  <GoogleAuthButton 
                    onClick={handleGoogleSignup}
                    disabled={isLoading}
                  />
                  <MicrosoftAuthButton 
                    onClick={handleMicrosoftSignup}
                    disabled={isLoading}
                  />
                  <AppleAuthButton 
                    onClick={handleAppleSignup}
                    disabled={isLoading}
                  />
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm mt-4">
                      <span className="px-2 bg-white text-gray-500">Or sign up with email</span>
                    </div>
                  </div>
                </div>
              )}

              {errors.general && (
                <div className={`rounded-lg p-4 ${
                  isOAuthSignup 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-danger-50 border border-danger-200'
                }`}>
                  <p className={`text-sm ${
                    isOAuthSignup 
                      ? 'text-green-600' 
                      : 'text-danger-600'
                  }`}>{errors.general}</p>
                </div>
              )}

              <div>
                <label htmlFor="customerName" className="form-label">
                  Company Name
                </label>
                <input
                  id="customerName"
                  name="customerName"
                  type="text"
                  required
                  className={`form-input ${errors.customerName ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  placeholder="Your construction company name"
                  value={formData.customerName}
                  onChange={handleInputChange}
                />
                {errors.customerName && (
                  <p className="form-error">{errors.customerName}</p>
                )}
              </div>

              <div>
                <label htmlFor="customerAddress" className="form-label">
                  Company Address
                </label>
                <textarea
                  id="customerAddress"
                  name="customerAddress"
                  required
                  rows={3}
                  className={`form-input ${errors.customerAddress ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  placeholder="Full company address"
                  value={formData.customerAddress}
                  onChange={handleInputChange}
                />
                {errors.customerAddress && (
                  <p className="form-error">{errors.customerAddress}</p>
                )}
              </div>

              <div>
                <label htmlFor="userName" className="form-label">
                  Your Name
                </label>
                <input
                  id="userName"
                  name="userName"
                  type="text"
                  required
                  className={`form-input ${errors.userName ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  placeholder="Your full name"
                  value={formData.userName}
                  onChange={handleInputChange}
                />
                {errors.userName && (
                  <p className="form-error">{errors.userName}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`form-input ${errors.email ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  placeholder="your.email@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && (
                  <p className="form-error">{errors.email}</p>
                )}
              </div>

              {/* Only show password fields for regular signups, not Google signups */}
              {!isOAuthSignup && (
                <>
                  <div>
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      className={`form-input ${errors.password ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                      placeholder="Create a secure password"
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    {errors.password && (
                      <p className="form-error">{errors.password}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password
                    </label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      className={`form-input ${errors.confirmPassword ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                    />
                    {errors.confirmPassword && (
                      <p className="form-error">{errors.confirmPassword}</p>
                    )}
                  </div>
                </>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary w-full"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {isOAuthSignup ? 'Completing OAuth Signup...' : 'Creating Account...'}
                    </div>
                  ) : (
                    isOAuthSignup ? 'Complete Your Signup' : 'Complete Registration'
                  )}
                </button>
              </div>
            </form>
            )}

            {/* Google Signup Button - only show for regular signups */}
            
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Already have an account? Sign in</span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  Sign in to your account
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            By creating an account, you agree to our{' '}
            <button type="button" className="text-primary-600 hover:text-primary-500 underline">
              Terms of Service
            </button>{' '}
            and{' '}
            <button type="button" className="text-primary-600 hover:text-primary-500 underline">
              Privacy Policy
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
